import { FileText } from 'lucide-react'
import { DataTable, Modal, type DataTableColumn } from '@atrvd/saas-ui'
import { formatYMDtoMDY } from './format-utils'
import { useFileImportTransactions, useFileImportTransactionsCount } from '@/hooks/useBanking'
import { useTablePagination } from '@/hooks/useTablePagination'
import { pageOrCountError, queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import type { TenantTransaction } from '@/lib/db-tenant'
import type { FileImportRow } from '@/types/banking'
import { formatCategoryAmount } from '@dcp/shared/utils/formatters'

// `formatCategoryAmount` is the single source of truth for rendering a
// category-aware transaction amount (text + Tailwind className). Lives in
// `@dcp/shared/utils/formatters` so this modal and ManualTransactionsTable
// can't drift on sign / locale / parens formatting independently. See its
// docblock for the full DCP-124 contract.

const COLUMNS: DataTableColumn<TenantTransaction>[] = [
  {
    key: 'transaction_date',
    header: 'Date',
    render: (tx) => formatYMDtoMDY(tx.transaction_date),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'description',
    header: 'Description',
    render: (tx) => (
      <div className="max-w-[250px] truncate" title={tx.description}>
        {tx.description}
      </div>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    render: (tx) => {
      const amt = formatCategoryAmount(tx.amount, tx.categories?.type)
      return <span className={amt.className}>{amt.text}</span>
    },
    cellClassName: 'whitespace-nowrap font-medium',
  },
  {
    // The facility is the linked ACCOUNT's — a transaction carries none of its
    // own. An imported row whose account is unassigned shows "-" here and still
    // appears: this is the import's audit view, so it lists everything the
    // import produced.
    key: 'facility',
    header: 'Facility',
    render: (tx) => tx.bank_accounts?.facilities?.name ?? '-',
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'account',
    header: 'Account',
    render: (tx) => tx.bank_accounts?.name ?? '-',
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'category',
    header: 'Category',
    render: (tx) => tx.categories?.name ?? '-',
    cellClassName: 'whitespace-nowrap',
  },
]

const EMPTY_TRANSACTIONS: TenantTransaction[] = []

/**
 * Modal showing paginated transactions for a specific file import.
 * Displays transaction date, description, amount, facility, bank account, and category.
 *
 * @param fileImport - The file import record to show transactions for (null = closed)
 * @param onClose - Callback to close the modal
 */
export function TransactionImportDetailsModal({
  fileImport,
  onClose,
}: {
  fileImport: FileImportRow | null
  onClose: () => void
}) {
  const {
    data: txTotal,
    isFetching: countFetching,
    error: countError,
    refetch: refetchCount,
  } = useFileImportTransactionsCount(fileImport?.id ?? null)
  // Another import is another result set: start it on page 1.
  const pagination = useTablePagination({ totalItems: txTotal, resetKey: fileImport?.id })
  const rowsQuery = useFileImportTransactions(
    fileImport?.id ?? null,
    { limit: pagination.pageSize, offset: pagination.offset },
  )
  const rows = rowsQuery.data ?? EMPTY_TRANSACTIONS
  const tableState = tableQueryState(
    {
      isPending: rowsQuery.isPending,
      isPlaceholderData: rowsQuery.isPlaceholderData,
      isFetching: rowsQuery.isFetching || countFetching,
      error: pageOrCountError(rowsQuery.error, countError),
      refetch: () => {
        void rowsQuery.refetch()
        void refetchCount()
      },
    },
    rows.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, rows.length)

  return (
    <Modal
      isOpen={!!fileImport}
      onClose={onClose}
      title={`Imported Transactions — ${fileImport?.file_name ?? ''}`}
      size="xl"
      className="!max-w-[1100px]"
    >
      <div className="space-y-4">
        {/* No count yet (or the count failed): saying "0" next to real rows would be false. */}
        {txTotal !== undefined && (
          <p className="text-sm text-muted-foreground">
            {txTotal} transaction{txTotal !== 1 ? 's' : ''} imported from this file
          </p>
        )}

        {errorDisplay.banner}
        <DataTable
          columns={COLUMNS}
          data={rows}
          rowKey={(tx) => tx.id}
          loading={tableState.loading}
          refreshing={tableState.refreshing}
          // Compact cells: DataTable's default p-4 grows rows to ~53px and
          // widens the six columns past the modal, clipping Category.
          density="compact"
          // Scroll with the Modal body: a scroller of its own would nest a second
          // scrollbar and capture the sticky header.
          scroll="parent"
          stickyHeader
          error={errorDisplay.tableError}
          emptyState={
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No transactions found</p>
            </div>
          }
          itemName="transactions"
          pagination={pagination.dataTablePagination}
        />
      </div>
    </Modal>
  )
}
